import React, { useEffect, useState } from "react";
import api, { fileUrl } from "@/lib/apiClient";
import { equipmentImage } from "@/lib/equipmentImage";
import { SUPPORT_WHATSAPP } from "@/lib/config";
import {
  FileText, CalendarClock, ShieldCheck, ClipboardList, DollarSign, CheckCircle2,
  Wind, ChevronRight, Download, Headphones, Package,
} from "lucide-react";

const INCLUDES = [
  "Manutenções preventivas periódicas",
  "Verificação de componentes elétricos e mecânicos",
  "Limpeza e higienização dos equipamentos",
  "Mão de obra especializada",
  "Atendimento prioritário",
  "Descontos em peças e serviços avulsos",
];

export default function ContractOverview({ equipment = [], onNav, onOpenEquip }) {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/me/contract").then(({ data }) => setC(data)).catch(() => {}); }, []);

  const tiles = [
    { icon: CalendarClock, label: "Vigência do contrato", value: c?.valid_from || "—", sub: c?.valid_to ? `até ${c.valid_to}` : "" },
    { icon: ShieldCheck, label: "Tipo de contrato", value: c?.contract_type || "—" },
    { icon: ClipboardList, label: "Plano contratado", value: c?.plan || "—" },
    { icon: DollarSign, label: "Forma de pagamento", value: c?.payment_method || "—" },
  ];
  const docs = c?.documents || [];

  return (
    <div className="space-y-5 pb-2" data-testid="contract-overview">
      {/* Cartão do contrato */}
      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5" data-testid="contract-card">
        <div className="flex items-start gap-4">
          <div className="h-16 w-16 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0 relative">
            <FileText className="h-8 w-8 text-[#1e7be0]" />
            <CheckCircle2 className="h-5 w-5 text-emerald-500 absolute -bottom-0.5 -right-0.5 bg-white rounded-full" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wide text-blue-700 bg-blue-100 rounded px-2 py-0.5">Contrato ativo</span>
              <span className="text-xs font-medium text-emerald-600 flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Ativo</span>
            </div>
            <h2 className="text-lg font-bold text-slate-800 mt-1">{c?.contract_type || "Contrato de Prestação de Serviços"}</h2>
            <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
              <div><p className="text-xs text-slate-400">Nº do contrato</p><p className="font-semibold text-slate-700">{c?.contract_number || "—"}</p></div>
              <div><p className="text-xs text-slate-400">Assinatura</p><p className="font-semibold text-slate-700">{c?.signed_date || "—"}</p></div>
            </div>
          </div>
        </div>
      </div>

      {/* Tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {tiles.map((t) => (
          <div key={t.label} className="rounded-xl bg-white border border-slate-200 p-3 text-center">
            <t.icon className="h-6 w-6 text-[#1e7be0] mx-auto mb-1.5" />
            <p className="text-[11px] text-slate-400">{t.label}</p>
            <p className="text-sm font-semibold text-slate-800 leading-tight">{t.value}</p>
            {t.sub && <p className="text-[11px] text-slate-500">{t.sub}</p>}
          </div>
        ))}
      </div>

      {/* Equipamentos cobertos */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold text-slate-800">Equipamentos cobertos pelo contrato</h3>
          <button onClick={() => onNav?.("equipamentos")} className="text-sm text-[#1e7be0] hover:underline flex items-center gap-0.5" data-testid="contract-see-equip">
            Ver todos ({equipment.length}) <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <div className="rounded-2xl bg-white border border-slate-200 divide-y divide-slate-100">
          {equipment.length === 0 ? (
            <div className="p-6 text-center text-slate-500 text-sm"><Package className="h-7 w-7 text-slate-300 mx-auto mb-1.5" />Nenhum equipamento cadastrado ainda.</div>
          ) : equipment.slice(0, 5).map((eq) => (
            <div key={eq.id} className="flex items-center gap-3 p-3" data-testid={`contract-ov-equip-${eq.id}`}>
              <span className="h-11 w-11 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                {equipmentImage(eq.equipment_type)
                  ? <img src={equipmentImage(eq.equipment_type)} alt={eq.equipment_type} className="h-full w-full object-contain" />
                  : <Wind className="h-6 w-6 text-[#1e7be0]" />}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800 text-sm truncate">{eq.equipment_type}{eq.location ? ` ${eq.location}` : ""}</p>
                <p className="text-xs text-slate-500 truncate">{[eq.brand, eq.model].filter(Boolean).join(" ")}{eq.capacity_btu ? ` · ${eq.capacity_btu} BTU` : ""}</p>
                {eq.patrimonio && <p className="text-xs text-slate-400">Patrimônio: {eq.patrimonio}</p>}
              </div>
              <span className="text-[10px] font-medium text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">Ativo</span>
            </div>
          ))}
        </div>
      </div>

      {/* O que inclui + Documentos */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl bg-white border border-slate-200 p-4">
          <h3 className="font-bold text-slate-800 mb-2">O que o contrato inclui</h3>
          <ul className="space-y-1.5">
            {INCLUDES.map((i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><CheckCircle2 className="h-4 w-4 text-[#1e7be0] mt-0.5 flex-shrink-0" />{i}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl bg-white border border-slate-200 p-4">
          <h3 className="font-bold text-slate-800 mb-2">Documentos importantes</h3>
          {docs.length === 0 ? (
            <p className="text-sm text-slate-400">Nenhum documento disponível ainda.</p>
          ) : (
            <div className="space-y-2">
              {docs.map((d) => (
                <a key={d.id} href={fileUrl(d.path)} target="_blank" rel="noreferrer"
                  className="flex items-center gap-3 rounded-lg border border-slate-100 p-2.5 hover:bg-slate-50" data-testid={`contract-ov-doc-${d.id}`}>
                  <FileText className="h-5 w-5 text-red-500 flex-shrink-0" />
                  <span className="flex-1 min-w-0"><span className="block text-sm font-medium text-slate-800 truncate">{d.type}</span><span className="block text-xs text-slate-400 truncate">{d.name}</span></span>
                  <Download className="h-4 w-4 text-slate-400 flex-shrink-0" />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Suporte */}
      <button onClick={() => window.open(`https://wa.me/${SUPPORT_WHATSAPP.replace(/\D/g, "")}?text=${encodeURIComponent("Olá! Tenho dúvidas sobre meu contrato Clima Branca.")}`, "_blank")}
        data-testid="contract-support-button"
        className="w-full text-left rounded-2xl bg-blue-50 border border-blue-100 p-4 flex items-center gap-4 hover:bg-blue-100 transition-colors">
        <Headphones className="h-8 w-8 text-[#1e7be0] flex-shrink-0" />
        <div className="flex-1 min-w-0"><p className="font-bold text-slate-800">Dúvidas sobre seu contrato?</p><p className="text-xs text-slate-500">Nossa equipe está pronta para ajudar você.</p></div>
        <span className="inline-flex items-center gap-1.5 rounded-xl bg-[#1e7be0] text-white px-3 py-2 text-sm font-semibold"><Headphones className="h-4 w-4" /> Falar com suporte</span>
      </button>
    </div>
  );
}
